import React, { useState, useMemo } from 'react';
import {
  FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Divider, TextField,
  InputAdornment, FormHelperText,
} from '@mui/material';
import {
  AccountBalanceOutlined as DistrictIcon,
  SearchOutlined as SearchIcon,
  InboxOutlined as EmptyIcon,
} from '@mui/icons-material';

export default function DistrictSelect({
  districts   = [],
  value       = '',
  onChange,
  label       = 'District',
  placeholder = 'All districts',
  disabled    = false,
  loading     = false,
  size        = 'small',
  fullWidth   = true,
  required    = false,
  error       = false,
  helperText  = null,
  name        = 'district_id',
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search.trim()) return districts;
    const q = search.toLowerCase();
    return districts.filter((d) => d.name?.toLowerCase().includes(q));
  }, [districts, search]);

  const handleClose = () => setSearch('');

  return (
    <FormControl
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      required={required}
      error={error}
    >
      <InputLabel>{loading ? 'Loading districts…' : label}</InputLabel>
      <Select
        name={name}
        value={value}
        label={loading ? 'Loading districts…' : label}
        onChange={(e) => onChange?.(e.target.value, e)}
        onClose={handleClose}
        startAdornment={
          <DistrictIcon sx={{ fontSize: 16, color: 'text.secondary', mr: 0.5 }} />
        }
        MenuProps={{
          autoFocus: false,
          PaperProps: { sx: { maxHeight: 340, borderRadius: 2 } },
        }}
        displayEmpty
      >
        {/* ── Search ── */}
        <Box
          sx={{ px: 1.5, pt: 1, pb: 0.5, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper' }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Search districts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13 } }}
          />
        </Box>

        <Divider sx={{ mb: 0.5 }} />

        {/* ── Placeholder ── */}
        <MenuItem value="">
          <Typography variant="body2" color="text.secondary">{placeholder}</Typography>
        </MenuItem>

        {/* ── Filtered results ── */}
        {filtered.length === 0 ? (
          <Box sx={{ px: 2, py: 2.5, textAlign: 'center' }}>
            <EmptyIcon sx={{ fontSize: 24, color: 'text.disabled', mb: 0.5 }} />
            <Typography variant="caption" color="text.disabled" display="block">
              No districts match "{search}"
            </Typography>
          </Box>
        ) : (
          filtered.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              <Typography variant="body2">{d.name}</Typography>
            </MenuItem>
          ))
        )}

        {/* ── Count ── */}
        {search && filtered.length > 0 && (
          <Box sx={{ px: 2, py: 0.75, borderTop: '0.5px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.disabled">
              {filtered.length} of {districts.length} district{districts.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        )}
      </Select>

      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}